import React, { useEffect, useState } from "react";
import {
  Send,
  Eye,
  Trash2,
  Upload,
  CheckSquare,
  Square,
  Download,
  Clock,
  CheckCircle2,
  BarChart3,
  Settings,
} from "lucide-react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import "./app.css";
import Loading from "./loading";
import { secretKey } from "./data";
import { downloadExcel } from "react-export-table-to-excel";
import LeadDetail from "./leadDetail";
import SendClientsConf from "./sendClientsConf";
import SendLeadsDetails from "./sendLeadsDetails";
import ImportLeadSend from "./importLeadsSend";
import SendClientsResults from "./sendClientsResults";

const MySwal = withReactContent(Swal2);
const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };

const TYPES = [
  { value: "CHF", label: "CHF" },
  { value: "SKD", label: "SKD" },
  { value: "WIBOR", label: "WIBOR" },
  { value: "SLUZEBNOSC", label: "SŁUŻEBNOŚĆ" },
];

const TABS = [
  { id: "PENDING", label: "Kolejka do wysyłki", icon: Clock },
  { id: "FINISHED", label: "Leady wysłane", icon: CheckCircle2 },
  { id: "RESULTS", label: "Wyniki", icon: BarChart3 },
  { id: "CONF", label: "Klienci conf.", icon: Settings },
];

const SOURCE_NOTE_LABELS = [
  { key: "bank", label: "Bank", company: "SKD,WIBOR,CHF" },
  { key: "ammount", label: "Kwota", company: "SKD,WIBOR,CHF" },
  { key: "creditDate", label: "Data kredytu", company: "SKD,WIBOR,CHF" },
  { key: "company", label: "Firmowy", company: "SKD,WIBOR,CHF" },
  { key: "change", label: "Przewalutowany", company: "CHF" },
  { key: "region", label: "Województwo", company: "SKD,WIBOR,CHF" },
];

const formatAdditional = (raw, lead) => {
  if (!raw) return null;
  try {
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    return SOURCE_NOTE_LABELS.filter((f) =>
      f.company.split(",").includes(lead.company)
    )
      .filter((f) => data[f.key])
      .map((f) => (
        <span key={f.key} className="ld-sn-tag">
          <strong>{f.label}:</strong> {data[f.key]}
        </span>
      ));
  } catch {
    return raw;
  }
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

/* ─── Lead card (shared between pending & sent) ─── */
const LeadCard = ({
  lead,
  checked,
  onCheck,
  onView,
  onDelete,
  showCheck = true,
}) => (
  <div className="sl-card">
    {showCheck && (
      <div className="sl-card-check">
        <input
          type="checkbox"
          className="db-check"
          checked={checked}
          onChange={() => onCheck(lead.id)}
        />
      </div>
    )}
    <div className="sl-card-fields">
      <div className="sl-card-field sl-card-field--name">
        <span className="db-lead-label">Nazwa</span>
        <span className="db-lead-value">
          {(lead.name || "").substring(0, 30)}
        </span>
        <button className="db-icon-btn" onClick={() => onView(lead)}>
          <Eye size={14} />
        </button>
      </div>
      <div className="sl-card-field sl-card-field--phone">
        <span className="db-lead-label">Telefon</span>
        <span className="db-lead-value">{lead.phone}</span>
      </div>
      <div className="sl-card-field sl-card-field--mail">
        <span className="db-lead-label">E-mail</span>
        <span className="db-lead-value" style={{ wordBreak: "break-all" }}>
          {lead.mail}
        </span>
      </div>
      <div className="sl-card-field sl-card-field--note">
        <span className="db-lead-label">Notatka</span>
        <span className="db-lead-value">
          {(lead.statusNote || "").substring(0, 80)}
          {lead.statusNote?.length > 80 ? "…" : ""}
        </span>
      </div>
      <div className="sl-card-field sl-card-field--note">
        <span className="db-lead-label">Walidacja</span>
        <span className="db-lead-value">
          {lead.validation
            ? formatAdditional(lead.validation)
            : "brak walidacji"}
        </span>
      </div>

      <div className="sl-card-field sl-card-field--status">
        <span className="db-lead-label">Status</span>
        <span className="db-lead-value">{lead.status}</span>
      </div>
      {onDelete && (
        <div className="sl-card-field sl-card-field--action">
          <button
            className="tc-action-btn tc-action-btn--accept"
            style={{ color: "#ef4444", borderColor: "#ef4444" }}
            onClick={() => onDelete(lead)}
          >
            <Trash2 size={13} /> Usuń
          </button>
        </div>
      )}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════ */

const SendLeads = (props) => {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("PENDING");
  const [sendType, setSendType] = useState("CHF");
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [company, setCompany] = useState("");

  /* modals */
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [leadDetail, setLeadDetail] = useState(null);
  const [showSendDetails, setShowSendDetails] = useState(false);
  const [showImport, setShowImport] = useState(false);

  /* checkboxes */
  const [chosen, setChosen] = useState([]);

  const chosenFlag = chosen.length > 0;

  /* ══════════ FETCH ══════════ */

  const fetchClients = async (option) => {
    try {
      const res = await fetch(`${API}/getSendClientsConf`, {
        method: "POST",
        headers: hdrs,
        body: "{}",
      });
      const data = await res.json();
      const opt = option || tab;
      setClients(
        opt === "PENDING"
          ? (data || []).filter((el) => +el.active === 1)
          : data || []
      );
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLeadsTemp = async () => {
    try {
      const res = await fetch(`${API}/getLeadsSendTemp`, {
        method: "POST",
        headers: hdrs,
        body: "{}",
      });
      setLeads(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLeadsSend = async () => {
    try {
      const res = await fetch(`${API}/getLeadsSendSend`, {
        method: "POST",
        headers: hdrs,
        body: JSON.stringify({ from, to, type: sendType, company }),
      });
      setLeads(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  const getData = async (option) => {
    setLoading(true);
    await fetchClients(option);
    const opt = option || tab;
    if (opt === "PENDING") await fetchLeadsTemp();
    else if (opt === "FINISHED") await fetchLeadsSend();
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  /* ══════════ HANDLERS ══════════ */

  const switchTab = (id) => {
    setTab(id);
    setChosen([]);
    setSendType("CHF");
    setCompany("");
    setFrom(todayStr());
    setTo(todayStr());
    if (id === "PENDING" || id === "FINISHED") getData(id);
  };

  const toggleCheck = (id) => {
    setChosen((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredLeads = leads.filter((el) => el.company === sendType);

  const selectAll = () => setChosen(filteredLeads.map((l) => l.id));
  const deselectAll = () => {
    setChosen([]);
    getData();
  };

  const viewLead = (lead) => {
    setLeadDetail(lead);
    setShowLeadDetail(true);
  };

  const deleteLead = async (lead) => {
    await fetch(`${API}/deleteFromSend`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify({ id: lead.id, source: lead.source }),
    }).catch(() => {});
    getData();
  };

  const showResults = () => {
    if (!from || !to) return;
    if (new Date(from) > new Date(to)) {
      MySwal.fire({
        icon: "error",
        title: "Błąd dat",
        text: '"Od" nie może być większe niż "Do"',
      });
      return;
    }
    getData();
  };

  const exportToExcel = () => {
    const clientName = clients.find((c) => +c.id === +company)?.name || "";
    const header = [
      "ID",
      "Imię i nazwisko",
      "Telefon",
      "E-mail",
      "Status",
      "Źródło",
      "Notatka-źródło",
      "Data",
      "Notatka",
      "Dział",
      "LINK",
      "Firma",
    ];
    const body = leads.map((el) => [
      el.id,
      el.name,
      el.phone,
      el.mail,
      el.status,
      el.source,
      el.sourceNote,
      el.date,
      el.statusNote,
      el.company,
      el.sourceLink,
      clientName,
    ]);
    downloadExcel({
      fileName: "leads_send_" + todayStr(),
      sheet: "data",
      tablePayload: { header, body },
    });
  };

  /* ══════════ RENDER ══════════ */

  /* Lead detail modal */
  if (showLeadDetail && leadDetail) {
    return (
      <div
        className="ld-modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowLeadDetail(false);
            setLeadDetail(null);
          }
        }}
      >
        <div className="ld-modal">
          <LeadDetail
            close={() => {
              setShowLeadDetail(false);
              setLeadDetail(null);
            }}
            lead={leadDetail}
            user={props.user}
          />
        </div>
      </div>
    );
  }

  if (showImport) {
    return (
      <ImportLeadSend
        close={() => {
          setShowImport(false);
          getData();
        }}
      />
    );
  }

  if (loading) return <Loading />;

  return (
    <div className="sl-wrapper">
      {/* ── Tabs ── */}
      <div className="sl-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              className={`tc-tab ${tab === t.id ? "tc-tab--active" : ""}`}
              onClick={() => switchTab(t.id)}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ══════ PENDING ══════ */}
      {tab === "PENDING" && (
        <div className="sl-content">
          <div className="sl-toolbar">
            <div className="sl-toolbar-left">
              <select
                className="db-input db-select"
                value={sendType}
                onChange={(e) => {
                  setSendType(e.target.value);
                  setChosen([]);
                }}
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <span className="lq-count">{filteredLeads.length} leadów</span>
            </div>
            <div className="sl-toolbar-right">
              <button
                className="lq-btn lq-btn--success"
                onClick={() => setShowImport(true)}
              >
                <Upload size={14} /> Importuj
              </button>
              <button className="lq-btn lq-btn--muted" onClick={selectAll}>
                <CheckSquare size={14} /> Zaznacz wszystkie
              </button>
              <button className="lq-btn lq-btn--muted" onClick={deselectAll}>
                <Square size={14} /> Odznacz
              </button>
              <button className="lq-btn lq-btn--success" onClick={getData}>
                Odśwież
              </button>
            </div>
          </div>

          {showSendDetails && (
            <SendLeadsDetails
              close={() => {
                setShowSendDetails(false);
                getData();
              }}
              sendOption={sendType}
              leads={filteredLeads.filter((l) => chosen.includes(l.id))}
              clients={clients.filter((c) => c.company === sendType)}
            />
          )}

          <div className="sl-list">
            {filteredLeads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                checked={chosen.includes(lead.id)}
                onCheck={toggleCheck}
                onView={viewLead}
                onDelete={deleteLead}
              />
            ))}
            {filteredLeads.length === 0 && (
              <div className="cr-empty">
                <p>Brak leadów w kolejce</p>
              </div>
            )}
          </div>

          {/* floating send button */}
          {chosenFlag && (
            <div
              className="db-float-action db-float-action--orange"
              style={{ top: "25vh" }}
              onClick={() => setShowSendDetails(true)}
            >
              <span>
                <Send size={16} /> WYŚLIJ ({chosen.length})
              </span>
            </div>
          )}
        </div>
      )}

      {/* ══════ FINISHED ══════ */}
      {tab === "FINISHED" && (
        <div className="sl-content">
          <div className="sl-toolbar">
            <div className="sl-toolbar-left">
              <select
                className="db-input db-select"
                value={sendType}
                onChange={(e) => {
                  setSendType(e.target.value);
                  setLeads([]);
                  setCompany("");
                }}
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <select
                className="db-input db-select"
                value={company}
                onChange={(e) => {
                  setCompany(e.target.value);
                  setLeads([]);
                }}
              >
                <option value="">Wybierz klienta...</option>
                {clients
                  .filter((c) => c.company === sendType)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="sl-toolbar-right">
              <div className="db-date-range">
                <div className="db-date-field">
                  <span className="db-date-label">od</span>
                  <input
                    type="date"
                    className="db-input"
                    value={from}
                    onChange={(e) => {
                      setFrom(e.target.value);
                      setLeads([]);
                    }}
                  />
                </div>
                <div className="db-date-field">
                  <span className="db-date-label">do</span>
                  <input
                    type="date"
                    className="db-input"
                    value={to}
                    onChange={(e) => {
                      setTo(e.target.value);
                      setLeads([]);
                    }}
                  />
                </div>
                <button
                  className="lq-btn lq-btn--dark db-date-apply"
                  onClick={showResults}
                >
                  Pokaż
                </button>
              </div>
              <button
                className="lq-btn lq-btn--success"
                onClick={exportToExcel}
              >
                <Download size={14} /> Eksport
              </button>
            </div>
          </div>

          <span className="lq-count" style={{ padding: "0 4px" }}>
            {leads.filter((l) => l.company === sendType).length} leadów
          </span>

          <div className="sl-list">
            {leads
              .filter((l) => l.company === sendType)
              .map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  showCheck={false}
                  onView={viewLead}
                  onCheck={() => {}}
                />
              ))}
            {leads.filter((l) => l.company === sendType).length === 0 && (
              <div className="cr-empty">
                <p>Brak wysłanych leadów dla wybranych filtrów</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════ RESULTS ══════ */}
      {tab === "RESULTS" && (
        <div className="sl-content">
          <SendClientsResults />
        </div>
      )}

      {/* ══════ CONF ══════ */}
      {tab === "CONF" && (
        <div className="sl-content">
          <SendClientsConf />
        </div>
      )}
    </div>
  );
};

export default SendLeads;
